// =====================================================================
//  ADMIN CONSOLE, LEAVE SETUP
// ---------------------------------------------------------------------
//  Leave types, their entitlement policies, and the holiday calendar.
//
//  Until migration 0009 none of this was editable at all. Entitlements
//  were a constant compiled into the server:
//
//      const ENTITLEMENTS = [
//        { type: 'Sick Leave',   days: 12 },
//        { type: 'Casual Leave', days: 12 },
//        { type: 'Paid Leave',   days: 15 },
//      ];
//
//  so every organization on the platform had identical leave, changeable
//  only by editing source and redeploying. Requests were matched to a
//  type by substring, anything that wasn't "sick" or "casual" silently
//  counted against the paid allowance, including "Maternity Leave" and
//  any typo.
//
//  Three tabs because they are three different jobs: naming the kinds of
//  leave, deciding how much of each is granted, and declaring the days
//  the office is shut. The holiday calendar belongs here rather than
//  under Attendance because its first purpose is leave arithmetic: a
//  public holiday inside a leave request must not be deducted from the
//  employee's balance.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  CalendarOff,
  Check,
  Palmtree,
  Pencil,
  Plus,
  Scale,
  Trash2,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import {
  Button,
  Checkbox,
  Chip,
  ConfirmDialog,
  Field,
  Grid,
  Input,
  Modal,
  Notice,
  PageHeader,
  Panel,
  PanelBody,
  Select,
  Tabs,
  Toolbar,
  ToolbarSpacer,
  toast,
} from '../ui';
import { fmtDate, fmtNumber, titleCase } from '../format';
import { cssVar } from '../theme';

const ACCRUAL_OPTIONS = [
  { value: 'annual', label: 'All at once, on 1 January' },
  { value: 'monthly', label: 'Accrues monthly' },
  { value: 'none', label: 'No quota (untracked)' },
];

// Explains what each accrual method actually does to a balance, because
// "annual" vs "monthly" is the single most consequential choice on the form
// and the difference is not obvious from the word.
const ACCRUAL_HELP = {
  annual: 'The full quota is available from the start of the year.',
  monthly:
    'One twelfth of the quota becomes available per completed month, so somebody cannot take a year of leave in January.',
  none: 'Requests are recorded but nothing is deducted and no balance is shown.',
};

const EMPTY_TYPE = {
  name: '',
  code: '',
  color: '#0EA5A4',
  is_paid: true,
  requires_approval: true,
  allow_half_day: true,
  is_balance_tracked: true,
  min_notice_days: '0',
  max_consecutive_days: '',
  sort_order: '0',
};

const EMPTY_POLICY = {
  leave_type_id: '',
  name: '',
  annual_quota: '12',
  accrual_method: 'annual',
  carry_forward_limit: '0',
  probation_months: '0',
  prorate_first_year: true,
};

const EMPTY_HOLIDAY = { name: '', date: '', is_optional: false, location_id: '' };

/** Derives a machine key from a display name, matching the server's rule. */
const slugifyCode = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, 'x$1');

export default function LeaveSetupPage({ can }) {
  const queryClient = useQueryClient();
  const canConfigure = can('leave.configure');

  const [tab, setTab] = useState('types');

  const [typeForm, setTypeForm] = useState(null);
  const [policyForm, setPolicyForm] = useState(null);
  const [holidayForm, setHolidayForm] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [formError, setFormError] = useState('');

  /* ------------------------------ data ------------------------------- */

  const { data: typeData, isLoading: typesLoading } = useQuery({
    queryKey: ['admin', 'leave-types'],
    queryFn: () => backendApi.entities.LeaveType.page({ limit: 200 }),
  });
  const types = useMemo(() => typeData?.rows || [], [typeData]);

  const { data: policyData, isLoading: policiesLoading } = useQuery({
    queryKey: ['admin', 'leave-policies'],
    queryFn: () => backendApi.entities.LeavePolicy.page({ limit: 200 }),
  });
  const policies = useMemo(() => policyData?.rows || [], [policyData]);

  const { data: holidayData, isLoading: holidaysLoading } = useQuery({
    queryKey: ['admin', 'holidays'],
    queryFn: () => backendApi.entities.Holiday.page({ limit: 500, sort: 'date', order: 'asc' }),
  });
  const holidays = useMemo(() => holidayData?.rows || [], [holidayData]);

  const { data: locationData } = useQuery({
    queryKey: ['admin', 'locations-lite'],
    queryFn: () => backendApi.entities.Location.page({ limit: 200 }),
  });
  const locationOptions = useMemo(
    () => (locationData?.rows || []).map((l) => ({ value: String(l.id), label: l.name })),
    [locationData]
  );

  const typeById = useMemo(
    () => new Map(types.map((t) => [String(t.id), t])),
    [types]
  );

  // A type with no policy has no quota, so a balance of zero is all anyone
  // will ever see. Worth surfacing rather than leaving to be discovered.
  const typesMissingPolicy = useMemo(
    () =>
      types.filter(
        (t) => t.is_balance_tracked && !policies.some((p) => String(p.leave_type_id) === String(t.id))
      ),
    [types, policies]
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin'] });

  /* ---------------------------- mutations ---------------------------- */

  const saveType = useMutation({
    mutationFn: (form) => {
      const body = {
        name: form.name.trim(),
        code: (form.code.trim() || slugifyCode(form.name)).toLowerCase(),
        color: form.color,
        is_paid: form.is_paid,
        requires_approval: form.requires_approval,
        allow_half_day: form.allow_half_day,
        is_balance_tracked: form.is_balance_tracked,
        min_notice_days: Number(form.min_notice_days) || 0,
        // Blank means no cap, which the column stores as NULL.
        max_consecutive_days: form.max_consecutive_days
          ? Number(form.max_consecutive_days)
          : null,
        sort_order: Number(form.sort_order) || 0,
      };
      return typeForm?.id
        ? backendApi.entities.LeaveType.update(typeForm.id, body)
        : backendApi.entities.LeaveType.create(body);
    },
    onSuccess: () => {
      toast.success(typeForm?.id ? 'Leave type updated' : 'Leave type created');
      setTypeForm(null);
      refresh();
    },
    onError: (err) => setFormError(err.message || 'Could not save this leave type'),
  });

  const savePolicy = useMutation({
    mutationFn: (form) => {
      const body = {
        leave_type_id: Number(form.leave_type_id),
        name: form.name.trim() || `${typeById.get(form.leave_type_id)?.name || 'Leave'} policy`,
        annual_quota: Number(form.annual_quota) || 0,
        accrual_method: form.accrual_method,
        carry_forward_limit: Number(form.carry_forward_limit) || 0,
        probation_months: Number(form.probation_months) || 0,
        prorate_first_year: form.prorate_first_year,
      };
      return policyForm?.id
        ? backendApi.entities.LeavePolicy.update(policyForm.id, body)
        : backendApi.entities.LeavePolicy.create(body);
    },
    onSuccess: () => {
      toast.success(policyForm?.id ? 'Policy updated' : 'Policy created');
      setPolicyForm(null);
      refresh();
    },
    onError: (err) => setFormError(err.message || 'Could not save this policy'),
  });

  const saveHoliday = useMutation({
    mutationFn: (form) => {
      const body = {
        name: form.name.trim(),
        date: form.date,
        is_optional: form.is_optional,
        location_id: form.location_id ? Number(form.location_id) : null,
      };
      return holidayForm?.id
        ? backendApi.entities.Holiday.update(holidayForm.id, body)
        : backendApi.entities.Holiday.create(body);
    },
    onSuccess: () => {
      toast.success(holidayForm?.id ? 'Holiday updated' : 'Holiday added');
      setHolidayForm(null);
      refresh();
    },
    onError: (err) => setFormError(err.message || 'Could not save this holiday'),
  });

  const remove = useMutation({
    mutationFn: ({ kind, row }) => {
      if (kind === 'type') return backendApi.entities.LeaveType.delete(row.id);
      if (kind === 'policy') return backendApi.entities.LeavePolicy.delete(row.id);
      return backendApi.entities.Holiday.delete(row.id);
    },
    onSuccess: () => {
      toast.success('Removed');
      setPendingDelete(null);
      refresh();
    },
    onError: (err) => toast.error(err.message || 'Could not remove this'),
  });

  /* ----------------------------- columns ----------------------------- */

  const rowActions = (kind) => (row) =>
    canConfigure ? (
      <div style={{ display: 'flex', gap: 4 }}>
        <Button
          size="sm"
          variant="ghost"
          icon={Pencil}
          aria-label={`Edit ${row.name}`}
          onClick={() => {
            setFormError('');
            if (kind === 'type') {
              setTypeForm({
                id: row.id,
                name: row.name || '',
                code: row.code || '',
                color: row.color || '#0EA5A4',
                is_paid: row.is_paid !== false,
                requires_approval: row.requires_approval !== false,
                allow_half_day: row.allow_half_day !== false,
                is_balance_tracked: row.is_balance_tracked !== false,
                min_notice_days: String(row.min_notice_days ?? 0),
                max_consecutive_days:
                  row.max_consecutive_days == null ? '' : String(row.max_consecutive_days),
                sort_order: String(row.sort_order ?? 0),
              });
            } else if (kind === 'policy') {
              setPolicyForm({
                id: row.id,
                leave_type_id: String(row.leave_type_id || ''),
                name: row.name || '',
                annual_quota: String(row.annual_quota ?? 0),
                accrual_method: row.accrual_method || 'annual',
                carry_forward_limit: String(row.carry_forward_limit ?? 0),
                probation_months: String(row.probation_months ?? 0),
                prorate_first_year: row.prorate_first_year !== false,
              });
            } else {
              setHolidayForm({
                id: row.id,
                name: row.name || '',
                date: String(row.date || '').slice(0, 10),
                is_optional: row.is_optional === true,
                location_id: row.location_id ? String(row.location_id) : '',
              });
            }
          }}
        />
        <Button
          size="sm"
          variant="ghost"
          icon={Trash2}
          aria-label={`Remove ${row.name}`}
          onClick={() => setPendingDelete({ kind, row })}
        />
      </div>
    ) : undefined;

  const typeColumns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Leave type',
        width: 210,
        render: (row) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span
              aria-hidden="true"
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: row.color || cssVar.accent,
                flexShrink: 0,
              }}
            />
            <span style={{ fontWeight: 600, color: cssVar.inkStrong }}>{row.name}</span>
          </span>
        ),
        csv: (row) => row.name,
      },
      {
        key: 'code',
        label: 'Code',
        width: 110,
        // The code is what the API and the portal's chart address this type
        // by, so it is shown rather than hidden as an implementation detail.
        render: (row) => <span className="ac-mono">{row.code}</span>,
      },
      {
        key: 'quota',
        label: 'Quota',
        width: 110,
        align: 'right',
        sortable: false,
        render: (row) => {
          const policy = policies.find((p) => String(p.leave_type_id) === String(row.id));
          if (!row.is_balance_tracked) {
            return <span style={{ color: cssVar.inkFaint }}>Untracked</span>;
          }
          if (!policy) return <Chip tone="warning">No policy</Chip>;
          return <span className="ac-mono">{fmtNumber(policy.annual_quota)} days</span>;
        },
        csv: (row) => {
          const policy = policies.find((p) => String(p.leave_type_id) === String(row.id));
          return policy ? policy.annual_quota : '';
        },
      },
      {
        key: 'min_notice_days',
        label: 'Notice',
        width: 90,
        align: 'right',
        render: (row) =>
          row.min_notice_days > 0 ? `${row.min_notice_days}d` : <span style={{ color: cssVar.inkFaint }}>,</span>,
      },
      {
        key: 'max_consecutive_days',
        label: 'Max run',
        width: 90,
        align: 'right',
        render: (row) =>
          row.max_consecutive_days ? `${row.max_consecutive_days}d` : <span style={{ color: cssVar.inkFaint }}>,</span>,
      },
      {
        key: 'rules',
        label: 'Rules',
        sortable: false,
        render: (row) => (
          <span style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {row.is_paid === false ? <Chip tone="warning">Unpaid</Chip> : null}
            {row.requires_approval === false ? <Chip tone="info">Auto-approved</Chip> : null}
            {row.allow_half_day === false ? <Chip>Full days only</Chip> : null}
            {row.is_active === false ? <Chip tone="negative">Inactive</Chip> : null}
          </span>
        ),
        csv: () => '',
      },
    ],
    [policies]
  );

  const policyColumns = useMemo(
    () => [
      {
        key: 'leave_type_id',
        label: 'Applies to',
        width: 200,
        render: (row) => {
          const type = typeById.get(String(row.leave_type_id));
          return type ? (
            <span style={{ fontWeight: 600, color: cssVar.inkStrong }}>{type.name}</span>
          ) : (
            <Chip tone="negative">Type removed</Chip>
          );
        },
        csv: (row) => typeById.get(String(row.leave_type_id))?.name || '',
      },
      {
        key: 'annual_quota',
        label: 'Days / year',
        width: 110,
        align: 'right',
        render: (row) => <span className="ac-mono">{fmtNumber(row.annual_quota)}</span>,
      },
      {
        key: 'accrual_method',
        label: 'Accrual',
        width: 150,
        render: (row) => <Chip>{titleCase(row.accrual_method)}</Chip>,
      },
      {
        key: 'carry_forward_limit',
        label: 'Carry forward',
        width: 120,
        align: 'right',
        render: (row) =>
          Number(row.carry_forward_limit) > 0 ? (
            <span className="ac-mono">{fmtNumber(row.carry_forward_limit)} days</span>
          ) : (
            <span style={{ color: cssVar.inkFaint }}>None</span>
          ),
      },
      {
        key: 'probation_months',
        label: 'Probation',
        width: 100,
        align: 'right',
        render: (row) =>
          Number(row.probation_months) > 0 ? (
            `${row.probation_months} mo`
          ) : (
            <span style={{ color: cssVar.inkFaint }}>,</span>
          ),
      },
      {
        key: 'prorate_first_year',
        label: 'Joiners',
        sortable: false,
        render: (row) =>
          row.prorate_first_year !== false ? (
            <Chip tone="positive">Pro-rated</Chip>
          ) : (
            <Chip tone="warning">Full quota</Chip>
          ),
        csv: (row) => (row.prorate_first_year !== false ? 'pro-rated' : 'full'),
      },
    ],
    [typeById]
  );

  const holidayColumns = useMemo(
    () => [
      {
        key: 'date',
        label: 'Date',
        width: 150,
        render: (row) => (
          <span style={{ fontWeight: 600, color: cssVar.inkStrong }}>{fmtDate(row.date)}</span>
        ),
        csv: (row) => row.date,
      },
      {
        key: 'weekday',
        label: 'Day',
        width: 110,
        sortable: false,
        // A holiday that already falls on a weekend costs the organization
        // nothing, which is useful to see when planning the calendar.
        render: (row) => {
          const day = new Date(`${String(row.date).slice(0, 10)}T00:00:00`);
          const label = day.toLocaleDateString(undefined, { weekday: 'short' });
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          return isWeekend ? (
            <span style={{ color: cssVar.inkFaint }}>{label} (weekend)</span>
          ) : (
            label
          );
        },
        csv: () => '',
      },
      { key: 'name', label: 'Holiday' },
      {
        key: 'is_optional',
        label: 'Type',
        width: 150,
        render: (row) =>
          row.is_optional ? (
            <Chip tone="info">Optional / restricted</Chip>
          ) : (
            <Chip tone="positive">Company-wide</Chip>
          ),
        csv: (row) => (row.is_optional ? 'optional' : 'company-wide'),
      },
      {
        key: 'location_id',
        label: 'Location',
        width: 160,
        render: (row) =>
          row.location_id ? (
            locationOptions.find((l) => l.value === String(row.location_id))?.label || ','
          ) : (
            <span style={{ color: cssVar.inkFaint }}>All locations</span>
          ),
        csv: (row) => row.location_id || '',
      },
    ],
    [locationOptions]
  );

  const typeOptions = types.map((t) => ({ value: String(t.id), label: t.name }));

  return (
    <div className="ac-page">
      <PageHeader
        title="Leave Setup"
        sub="The kinds of leave your organization offers, how much of each is granted, and the days you are closed."
      />

      {!canConfigure ? (
        <div style={{ marginBottom: 14 }}>
          <Notice tone="info" title="You have read-only access here">
            Changing leave types, policies or the holiday calendar requires the
            leave configuration permission, which sits with administrators and HR.
          </Notice>
        </div>
      ) : null}

      <Tabs
        items={[
          { key: 'types', label: 'Leave types', count: types.length, icon: CalendarDays },
          { key: 'policies', label: 'Entitlements', count: policies.length, icon: Scale },
          { key: 'holidays', label: 'Holiday calendar', count: holidays.length, icon: Palmtree },
        ]}
        value={tab}
        onChange={setTab}
      />

      {/* ------------------------------ TYPES ------------------------------ */}
      {tab === 'types' ? (
        <>
          {typesMissingPolicy.length ? (
            <div style={{ marginBottom: 14 }}>
              <Notice
                tone="warning"
                title={`${typesMissingPolicy.length} leave type${
                  typesMissingPolicy.length === 1 ? '' : 's'
                } without an entitlement`}
                actions={
                  canConfigure ? (
                    <Button size="sm" onClick={() => setTab('policies')}>
                      Set entitlements
                    </Button>
                  ) : null
                }
              >
                {typesMissingPolicy.map((t) => t.name).join(', ')},{' '}
                {typesMissingPolicy.length === 1 ? 'this type has' : 'these types have'} no quota,
                so everyone will see a balance of zero and no leave can be taken against{' '}
                {typesMissingPolicy.length === 1 ? 'it' : 'them'}.
              </Notice>
            </div>
          ) : null}

          <Panel>
            <PanelBody flush>
              <DataTable
                columns={typeColumns}
                rows={types}
                loading={typesLoading}
                getRowId={(row) => row.id}
                defaultSort={{ key: 'sort_order', dir: 'asc' }}
                paginate={false}
                emptyIcon={CalendarDays}
                emptyTitle="No leave types yet"
                emptyText="Add a leave type to start building your leave policy."
                rowActions={rowActions('type')}
                toolbar={
                  <Toolbar>
                    <span style={{ fontSize: 12, color: cssVar.inkMuted }}>
                      The code is how the API and the employee portal refer to a type, so it stays
                      stable when you rename one.
                    </span>
                    <ToolbarSpacer />
                    {canConfigure ? (
                      <Button
                        variant="primary"
                        icon={Plus}
                        onClick={() => {
                          setFormError('');
                          setTypeForm({ ...EMPTY_TYPE });
                        }}
                      >
                        Add leave type
                      </Button>
                    ) : null}
                  </Toolbar>
                }
              />
            </PanelBody>
          </Panel>
        </>
      ) : null}

      {/* ---------------------------- POLICIES ----------------------------- */}
      {tab === 'policies' ? (
        <Panel>
          <PanelBody flush>
            <DataTable
              columns={policyColumns}
              rows={policies}
              loading={policiesLoading}
              getRowId={(row) => row.id}
              paginate={false}
              emptyIcon={Scale}
              emptyTitle="No entitlements set"
              emptyText="An entitlement says how many days of a leave type someone gets each year."
              rowActions={rowActions('policy')}
              toolbar={
                <Toolbar>
                  <span style={{ fontSize: 12, color: cssVar.inkMuted }}>
                    One entitlement per leave type.
                  </span>
                  <ToolbarSpacer />
                  {canConfigure ? (
                    <Button
                      variant="primary"
                      icon={Plus}
                      disabled={types.length === 0}
                      onClick={() => {
                        setFormError('');
                        setPolicyForm({ ...EMPTY_POLICY });
                      }}
                    >
                      Add entitlement
                    </Button>
                  ) : null}
                </Toolbar>
              }
            />
          </PanelBody>
        </Panel>
      ) : null}

      {/* ---------------------------- HOLIDAYS ----------------------------- */}
      {tab === 'holidays' ? (
        <>
          <div style={{ marginBottom: 14 }}>
            <Notice tone="default" icon={CalendarOff} title="Holidays are not deducted from leave">
              A company-wide holiday inside a leave request is not counted against the employee's
              balance. An optional holiday is theirs to choose, so it stays a working day unless
              they take it.
            </Notice>
          </div>

          <Panel>
            <PanelBody flush>
              <DataTable
                columns={holidayColumns}
                rows={holidays}
                loading={holidaysLoading}
                getRowId={(row) => row.id}
                defaultSort={{ key: 'date', dir: 'asc' }}
                emptyIcon={Palmtree}
                emptyTitle="No holidays yet"
                emptyText="Add the days your organization is closed so leave is counted correctly."
                rowActions={rowActions('holiday')}
                toolbar={
                  <Toolbar>
                    <ToolbarSpacer />
                    {canConfigure ? (
                      <Button
                        variant="primary"
                        icon={Plus}
                        onClick={() => {
                          setFormError('');
                          setHolidayForm({ ...EMPTY_HOLIDAY });
                        }}
                      >
                        Add holiday
                      </Button>
                    ) : null}
                  </Toolbar>
                }
              />
            </PanelBody>
          </Panel>
        </>
      ) : null}

      {/* ------------------------------ FORMS ------------------------------ */}

      <FormModal
        open={Boolean(typeForm)}
        title={typeForm?.id ? `Edit “${typeForm.name}”` : 'Add a leave type'}
        error={formError}
        busy={saveType.isPending}
        disabled={!typeForm?.name?.trim()}
        onClose={() => setTypeForm(null)}
        onSave={() => saveType.mutate(typeForm)}
      >
        {typeForm ? (
          <>
            <Grid cols={2}>
              <Field label="Name" required>
                <Input
                  value={typeForm.name}
                  onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                  autoFocus
                />
              </Field>
              <Field
                label="Code"
                hint={
                  typeForm.id
                    ? 'Changing this breaks anything referring to the old code.'
                    : `Leave empty to use "${slugifyCode(typeForm.name) || '…'}"`
                }
              >
                <Input
                  value={typeForm.code}
                  placeholder={slugifyCode(typeForm.name)}
                  onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value })}
                />
              </Field>
            </Grid>

            <Grid cols={3}>
              <Field label="Colour" hint="Used in the portal">
                <Input
                  type="color"
                  value={typeForm.color}
                  onChange={(e) => setTypeForm({ ...typeForm, color: e.target.value })}
                />
              </Field>
              <Field label="Notice required" hint="Days ahead">
                <Input
                  type="number"
                  min="0"
                  value={typeForm.min_notice_days}
                  onChange={(e) => setTypeForm({ ...typeForm, min_notice_days: e.target.value })}
                />
              </Field>
              <Field label="Max consecutive" hint="Blank for no limit">
                <Input
                  type="number"
                  min="1"
                  value={typeForm.max_consecutive_days}
                  onChange={(e) =>
                    setTypeForm({ ...typeForm, max_consecutive_days: e.target.value })
                  }
                />
              </Field>
            </Grid>

            <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
              <Checkbox
                checked={typeForm.is_paid}
                onChange={(e) => setTypeForm({ ...typeForm, is_paid: e.target.checked })}
                label="Paid leave"
              />
              <Checkbox
                checked={typeForm.is_balance_tracked}
                onChange={(e) =>
                  setTypeForm({ ...typeForm, is_balance_tracked: e.target.checked })
                }
                label="Track a balance"
              />
              <Checkbox
                checked={typeForm.requires_approval}
                onChange={(e) =>
                  setTypeForm({ ...typeForm, requires_approval: e.target.checked })
                }
                label="Needs approval"
              />
              <Checkbox
                checked={typeForm.allow_half_day}
                onChange={(e) => setTypeForm({ ...typeForm, allow_half_day: e.target.checked })}
                label="Allow half days"
              />
            </div>

            {!typeForm.is_balance_tracked ? (
              <div style={{ marginTop: 10 }}>
                <Notice tone="info">
                  With no balance tracked, requests of this type are recorded but nothing is
                  deducted and the portal shows no remaining days. Suitable for unpaid or
                  bereavement leave.
                </Notice>
              </div>
            ) : null}

            {!typeForm.requires_approval ? (
              <div style={{ marginTop: 10 }}>
                <Notice tone="warning">
                  Requests of this type take effect immediately, with no approval step and nobody
                  notified to review them.
                </Notice>
              </div>
            ) : null}
          </>
        ) : null}
      </FormModal>

      <FormModal
        open={Boolean(policyForm)}
        title={policyForm?.id ? 'Edit entitlement' : 'Add an entitlement'}
        error={formError}
        busy={savePolicy.isPending}
        disabled={!policyForm?.leave_type_id}
        onClose={() => setPolicyForm(null)}
        onSave={() => savePolicy.mutate(policyForm)}
      >
        {policyForm ? (
          <>
            <Field label="Leave type" required>
              <Select
                options={typeOptions}
                placeholder="Choose a type"
                value={policyForm.leave_type_id}
                // Changing the type on an existing entitlement would move
                // everyone's balance from one type to another.
                disabled={Boolean(policyForm.id)}
                onChange={(e) =>
                  setPolicyForm({ ...policyForm, leave_type_id: e.target.value })
                }
              />
            </Field>

            <Grid cols={2}>
              <Field label="Days per year" required>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={policyForm.annual_quota}
                  onChange={(e) =>
                    setPolicyForm({ ...policyForm, annual_quota: e.target.value })
                  }
                />
              </Field>
              <Field label="How it becomes available" hint={ACCRUAL_HELP[policyForm.accrual_method]}>
                <Select
                  options={ACCRUAL_OPTIONS}
                  value={policyForm.accrual_method}
                  onChange={(e) =>
                    setPolicyForm({ ...policyForm, accrual_method: e.target.value })
                  }
                />
              </Field>
            </Grid>

            <Grid cols={2}>
              <Field
                label="Carry forward"
                hint="Unused days that roll into next year. 0 means use it or lose it."
              >
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={policyForm.carry_forward_limit}
                  onChange={(e) =>
                    setPolicyForm({ ...policyForm, carry_forward_limit: e.target.value })
                  }
                />
              </Field>
              <Field label="Probation" hint="Months before any leave accrues">
                <Input
                  type="number"
                  min="0"
                  value={policyForm.probation_months}
                  onChange={(e) =>
                    setPolicyForm({ ...policyForm, probation_months: e.target.value })
                  }
                />
              </Field>
            </Grid>

            <Checkbox
              checked={policyForm.prorate_first_year}
              onChange={(e) =>
                setPolicyForm({ ...policyForm, prorate_first_year: e.target.checked })
              }
              label="Pro-rate the first year for new joiners"
            />
            <div style={{ fontSize: 12, color: cssVar.inkMuted, marginTop: 6 }}>
              {policyForm.prorate_first_year
                ? 'Somebody joining in October gets a share of the quota, not all of it.'
                : 'Somebody joining in October gets the full annual quota immediately.'}
            </div>

            {Number(policyForm.carry_forward_limit) > Number(policyForm.annual_quota) ? (
              <div style={{ marginTop: 10 }}>
                <Notice tone="negative">
                  Carry forward cannot exceed the annual quota. The server will reject this.
                </Notice>
              </div>
            ) : null}
          </>
        ) : null}
      </FormModal>

      <FormModal
        open={Boolean(holidayForm)}
        title={holidayForm?.id ? `Edit “${holidayForm.name}”` : 'Add a holiday'}
        error={formError}
        busy={saveHoliday.isPending}
        disabled={!holidayForm?.name?.trim() || !holidayForm?.date}
        onClose={() => setHolidayForm(null)}
        onSave={() => saveHoliday.mutate(holidayForm)}
      >
        {holidayForm ? (
          <>
            <Grid cols={2}>
              <Field label="Name" required>
                <Input
                  value={holidayForm.name}
                  onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                  autoFocus
                />
              </Field>
              <Field label="Date" required>
                <Input
                  type="date"
                  value={holidayForm.date}
                  onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })}
                />
              </Field>
            </Grid>

            <Field
              label="Location"
              hint="Leave empty to apply to everyone. Useful when offices in different places observe different days."
            >
              <Select
                options={locationOptions}
                placeholder="All locations"
                value={holidayForm.location_id}
                onChange={(e) =>
                  setHolidayForm({ ...holidayForm, location_id: e.target.value })
                }
              />
            </Field>

            <Checkbox
              checked={holidayForm.is_optional}
              onChange={(e) =>
                setHolidayForm({ ...holidayForm, is_optional: e.target.checked })
              }
              label="Optional / restricted holiday"
            />
            <div style={{ fontSize: 12, color: cssVar.inkMuted, marginTop: 6 }}>
              {holidayForm.is_optional
                ? 'Stays a working day. Employees choose whether to take it.'
                : 'The office is closed. Not deducted from anyone’s leave balance.'}
            </div>
          </>
        ) : null}
      </FormModal>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => remove.mutate(pendingDelete)}
        loading={remove.isPending}
        title={`Remove “${pendingDelete?.row?.name || ''}”?`}
        confirmLabel="Remove"
        message={
          pendingDelete?.kind === 'type'
            ? 'Past leave requests of this type are kept, but they will no longer be attached to a type and no further requests can be made against it.'
            : pendingDelete?.kind === 'policy'
              ? 'The leave type stays, but with no entitlement nobody will have a balance for it.'
              : 'This day becomes a normal working day again, and leave taken across it will be counted.'
        }
      />
    </div>
  );
}

/* =====================================================================
   A modal with the save/cancel footer every form here shares.
   ================================================================== */

function FormModal({ open, title, error, busy, disabled, onClose, onSave, children }) {
  // Wraps the shared Modal with the save/cancel footer and the error
  // notice, so the three forms on this page declare them once rather than
  // three times.
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" icon={Check} loading={busy} disabled={disabled} onClick={onSave}>
            Save
          </Button>
        </>
      }
    >
      {error ? (
        <div style={{ marginBottom: 12 }}>
          <Notice tone="negative" title="Could not save">
            {error}
          </Notice>
        </div>
      ) : null}
      {children}
    </Modal>
  );
}
